import { useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { Send } from 'lucide-react'
import { ErrorApi } from '@/api/errores'
import { formatearFecha } from '@/lib/fecha'
import { formatearRNC, limpiarRNC } from '@/lib/rnc'
import { Banner } from '@/ui/Banner'
import { Boton } from '@/ui/Boton'
import { CampoTexto } from '@/ui/CampoFormulario'
import { ConfirmacionDestructiva } from '@/ui/ConfirmacionDestructiva'
import { Esqueleto } from '@/ui/Esqueleto'
import { EstadoError } from '@/ui/EstadoError'
import { EstadoVacio } from '@/ui/EstadoVacio'
import { InsigniaEstado } from '@/ui/InsigniaEstado'
import { useAccionDelCliente, useInvitarDespacho, useVinculosDelCliente } from '@/dominio/despacho/useDespacho'
import { PRESENTACION_VINCULO, type VinculoCliente } from '@/dominio/despacho/tipos'
import { EncabezadoPantalla, Tarjeta } from './-pantalla'
import { ErrorEnLinea, mensajeDelServidor } from './despacho/-comun'

/**
 * El contador externo, visto desde el contribuyente (`docs/adr/0021` §2).
 *
 * Qué despacho contable lleva su contabilidad, **quién de ese despacho ve hoy
 * su información**, las invitaciones que le llegaron, invitar a uno, y revocar.
 * La revocación no pide permiso a nadie y surte efecto en la petición
 * siguiente: nadie del despacho vuelve a entrar.
 *
 * Lo decide el administrador del contribuyente, el mismo que decide quién de
 * dentro ve qué. Todo queda en la auditoría del contribuyente.
 */
export const Route = createFileRoute('/contador')({
  component: PantallaContador,
})

function PantallaContador() {
  const consulta = useVinculosDelCliente()
  const accion = useAccionDelCliente()
  const [revocando, setRevocando] = useState<VinculoCliente | null>(null)

  const todos = consulta.data ?? []
  const recibidas = todos.filter((v) => v.estado === 'pendiente' && v.iniciadoPor === 'despacho')
  const enviadas = todos.filter((v) => v.estado === 'pendiente' && v.iniciadoPor === 'cliente')
  const vinculados = todos.filter((v) => v.estado === 'aceptado')
  const historial = todos.filter((v) => v.estado !== 'pendiente' && v.estado !== 'aceptado')

  const sinPermiso = consulta.error instanceof ErrorApi && consulta.error.clase === 'permiso'

  return (
    <div className="flex max-w-[960px] flex-col gap-6">
      <EncabezadoPantalla
        titulo="Contador"
        descripcion="El despacho contable que lleva su contabilidad entra a sus comprobantes, sus reportes y su nómina con permisos de contador: no emite ni toca usuarios. Usted decide quién y lo revoca cuando quiera."
      />

      {sinPermiso ? (
        <EstadoVacio
          variante="sin-resultados"
          mensaje="Esta pantalla es del administrador del contribuyente: él decide qué despacho ve su información."
        />
      ) : consulta.isError ? (
        <EstadoError
          titulo="No se pudo cargar el contador."
          contexto={mensajeDelServidor(consulta.error) ?? 'Reintente en un momento.'}
          onReintentar={() => void consulta.refetch()}
        />
      ) : consulta.isLoading ? (
        <Esqueleto className="h-48 w-full" />
      ) : (
        <>
          {accion.isError ? <ErrorEnLinea error={accion.error} accion="hacer el cambio" /> : null}

          {recibidas.length > 0 ? (
            <Tarjeta titulo="Despachos que le proponen llevar su contabilidad">
              <ul className="flex flex-col divide-y divide-gris-200">
                {recibidas.map((v) => (
                  <li key={v.id} className="flex flex-col gap-2 py-3">
                    <Despacho vinculo={v} />
                    {!v.verificado ? (
                      <Banner tono="ambar" titulo="Despacho sin verificar">
                        Se registró desde el portal y Agentical Group todavía no comprobó su RNC. Acéptelo solo si conoce al despacho.
                      </Banner>
                    ) : null}
                    <span className="flex gap-2">
                      <Boton
                        variante="primario"
                        tamano="menor"
                        disabled={accion.isPending}
                        onClick={() => accion.mutate({ vinculo: v.id, accion: 'aceptar' })}
                      >
                        Aceptar
                      </Boton>
                      <Boton
                        tamano="menor"
                        disabled={accion.isPending}
                        onClick={() => accion.mutate({ vinculo: v.id, accion: 'rechazar' })}
                      >
                        Rechazar
                      </Boton>
                    </span>
                  </li>
                ))}
              </ul>
            </Tarjeta>
          ) : null}

          <Tarjeta titulo="Su despacho contable">
            {vinculados.length === 0 ? (
              <EstadoVacio variante="primera-vez" mensaje="Ningún despacho lleva su contabilidad en la plataforma." />
            ) : (
              <ul className="flex flex-col divide-y divide-gris-200">
                {vinculados.map((v) => (
                  <li key={v.id} className="flex flex-wrap items-start justify-between gap-3 py-3">
                    <span className="flex min-w-0 flex-col gap-1">
                      <Despacho vinculo={v} />
                      <span className="text-etiqueta text-gris-600">
                        Desde el {formatearFecha(v.respondidoEl ?? v.propuestoEl)}.{' '}
                        {v.conAcceso && v.conAcceso.length > 0
                          ? `Hoy ven su información: ${v.conAcceso.join(', ')}.`
                          : 'Hoy nadie del despacho ve su información.'}
                      </span>
                    </span>
                    <Boton variante="destructivo" tamano="menor" onClick={() => setRevocando(v)}>
                      Revocar
                    </Boton>
                  </li>
                ))}
              </ul>
            )}
          </Tarjeta>

          <InvitarDespacho />

          {enviadas.length > 0 ? (
            <Tarjeta titulo="Invitaciones que usted envió">
              <ul className="flex flex-col divide-y divide-gris-200">
                {enviadas.map((v) => (
                  <li key={v.id} className="flex flex-wrap items-center justify-between gap-3 py-2">
                    <Despacho vinculo={v} />
                    <Boton
                      tamano="menor"
                      variante="discreto"
                      disabled={accion.isPending}
                      onClick={() => accion.mutate({ vinculo: v.id, accion: 'retirar' })}
                    >
                      Retirar
                    </Boton>
                  </li>
                ))}
              </ul>
            </Tarjeta>
          ) : null}

          {historial.length > 0 ? (
            <Tarjeta titulo="Historial">
              <ul className="flex flex-col divide-y divide-gris-200">
                {historial.map((v) => (
                  <li key={v.id} className="flex flex-wrap items-center justify-between gap-3 py-2">
                    <Despacho vinculo={v} />
                    <span className="text-etiqueta text-gris-600">
                      {formatearFecha(v.terminadoEl ?? v.respondidoEl ?? v.propuestoEl)}
                      {v.terminadoLado ? ` · por ${v.terminadoLado === 'cliente' ? 'usted' : 'el despacho'}` : ''}
                      {v.motivoFin ? ` · ${v.motivoFin}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </Tarjeta>
          ) : null}
        </>
      )}

      <p className="text-etiqueta text-gris-600">
        ¿Lleva usted la contabilidad de otros contribuyentes?{' '}
        <Link to="/despacho" className="text-marca underline">
          Registre su despacho
        </Link>{' '}
        y vea a todos sus clientes en una pantalla.
      </p>

      {revocando ? (
        <ConfirmacionDestructiva
          abierto
          onCancelar={() => setRevocando(null)}
          onConfirmar={() =>
            accion.mutate(
              { vinculo: revocando.id, accion: 'revocar', motivo: 'Revocado por el administrador del contribuyente' },
              { onSuccess: () => setRevocando(null) },
            )
          }
          titulo="Revocar al despacho"
          descripcion={
            <p>
              Nadie de <strong>{revocando.nombre}</strong> vuelve a entrar a su información, desde ya. Queda en su
              auditoría. Para volver, el despacho tiene que invitarle otra vez y usted aceptar.
            </p>
          }
          textoAEscribir="revocar"
          etiquetaConfirmar="Revocar"
          trabajando={accion.isPending}
        />
      ) : null}
    </div>
  )
}

function Despacho({ vinculo }: { vinculo: VinculoCliente }) {
  const presentacion = PRESENTACION_VINCULO[vinculo.estado]
  return (
    <span className="flex min-w-0 flex-col">
      <span className="flex flex-wrap items-center gap-2">
        <span className="text-gris-900">{vinculo.nombre}</span>
        <InsigniaEstado tono={presentacion.tono}>{presentacion.etiqueta}</InsigniaEstado>
        {vinculo.verificado ? <InsigniaEstado tono="marca">verificado por Agentical Group</InsigniaEstado> : null}
      </span>
      <span className="numero text-etiqueta text-gris-600">
        {vinculo.tipoIdentificacion === 'rnc' ? 'RNC' : 'Cédula'} {formatearRNC(vinculo.identificacion)} · {vinculo.correo}
        {vinculo.mensaje ? ` · «${vinculo.mensaje}»` : ''}
      </span>
    </span>
  )
}

function InvitarDespacho() {
  const invitar = useInvitarDespacho()
  const [identificacion, setIdentificacion] = useState('')
  const [mensaje, setMensaje] = useState('')
  const digitos = limpiarRNC(identificacion)
  const estructura = digitos.length === 9 || digitos.length === 11

  return (
    <Tarjeta titulo="Invitar a su despacho">
      <form
        onSubmit={(e) => {
          e.preventDefault()
          invitar.mutate(
            { identificacion: digitos, mensaje: mensaje.trim() === '' ? undefined : mensaje.trim() },
            {
              onSuccess: () => {
                setIdentificacion('')
                setMensaje('')
              },
            },
          )
        }}
        className="flex flex-col gap-3"
      >
        <div className="grid gap-3 md:grid-cols-[220px_1fr]">
          <CampoTexto
            etiqueta="RNC o cédula del despacho"
            obligatorio
            numerico
            inputMode="numeric"
            value={identificacion}
            onChange={(e) => setIdentificacion(e.target.value)}
            error={identificacion !== '' && !estructura ? 'Nueve dígitos el RNC, once la cédula.' : undefined}
          />
          <CampoTexto
            etiqueta="Mensaje"
            anotacion="opcional"
            value={mensaje}
            maxLength={500}
            onChange={(e) => setMensaje(e.target.value)}
          />
        </div>
        {invitar.isSuccess ? (
          <p className="text-etiqueta text-verde">Invitación enviada. El despacho la acepta desde su cartera.</p>
        ) : null}
        {invitar.isError ? <ErrorEnLinea error={invitar.error} accion="invitar al despacho" /> : null}
        <div>
          <Boton type="submit" icono={<Send />} disabled={!estructura || invitar.isPending}>
            {invitar.isPending ? 'Enviando…' : 'Invitar'}
          </Boton>
        </div>
      </form>
    </Tarjeta>
  )
}
